import {SearchIcon} from '@primer/octicons-react'
import {FormControl} from '@primer/react'
import {type ComponentProps, useMemo} from 'react'

import type {SuggestedRepository} from '../../api/repository/contracts'
import {useAutomationGraph} from '../../state-providers/workflows/use-automation-graph'
import {TokenizedFilterInput} from '../filter-bar/tokenized-filter-input'
import {DisabledFilterInput} from './blocks/disabled-filter-input'
import {useSearchInputState} from './hooks/use-search-input-state'
import {getSupportedProviderKeys, useWorkflowQueryValidation} from './hooks/use-workflow-query-validation'
import styles from './search-input.module.css'

type SearchInputProps = ComponentProps<'div'> & {
  onQueryChange: (query: string) => void
  query: string
  initialQuery?: string
  repository?: SuggestedRepository
}

export const SearchInput = ({onQueryChange, query, initialQuery = '', repository}: SearchInputProps) => {
  const {workflow} = useAutomationGraph()

  const {isDirty, editingDisabled, resetChanges, onChangeQueryValue} = useSearchInputState({
    query,
    initialQuery,
    workflow,
    onQueryChange,
  })
  const {onValidation, errorMessage} = useWorkflowQueryValidation(workflow, isDirty)

  const supportedProviderKeys = useMemo(() => getSupportedProviderKeys(workflow), [workflow])

  if (editingDisabled) {
    return <DisabledFilterInput query={query} icon={<SearchIcon />} />
  } else {
    return (
      <>
        <TokenizedFilterInput
          value={query}
          onChangeValue={onChangeQueryValue}
          onResetChanges={isDirty ? resetChanges : undefined}
          hideSaveButton
          hideCounterLabel
          hideDisplayButton
          aria-describedby="query-validation-message"
          limitedProviderKeys={supportedProviderKeys}
          onValidation={onValidation}
          context={repository ? {repo: repository.nameWithOwner} : undefined}
        />
        {errorMessage && (
          <FormControl.Validation
            variant="error"
            id="query-validation-message"
            className={styles.FormControl_Validation}
          >
            {errorMessage}
          </FormControl.Validation>
        )}
      </>
    )
  }
}

try{ SearchInput.displayName ||= 'SearchInput' } catch {}